import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// How far the user must scroll down (accumulated) before the bars hide.
// Larger than the show threshold so casual touch jitter never hides them.
const HIDE_AFTER_PX = 24;
// How far the user must scroll up (accumulated) before the bars return.
const SHOW_AFTER_PX = 8;
// Within this distance from the top the bars are always shown.
const TOP_ZONE_PX = 80;

/**
 * Tracks the page scroll direction and reports whether the fixed bars
 * (top header, bottom nav, docked action bar) should be hidden to free
 * vertical space: hidden while scrolling down, shown again on the first
 * real upward scroll, always shown near the top of the page.
 *
 * The hook only computes the boolean; the visual effect is applied by
 * mobile-scoped CSS reacting to the .nav-hidden class on the layout,
 * so desktop rendering never changes.
 */
export function useAutoHideNav(enabled = true) {
    const [hidden, setHidden] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        if (!enabled) {
            setHidden(false);
            return undefined;
        }

        // Every page starts with the bars visible.
        setHidden(false);

        let lastY = window.scrollY;
        let accumulated = 0;

        // Runs directly on every scroll event: the work is a handful of
        // number compares, and frame-based throttling starves in embedded
        // webviews that pause animation frames while offscreen.
        const onScroll = () => {
            // Clamp so iOS rubber-band overscroll (negative on top, beyond the
            // document on bottom) never registers as a direction change.
            const maxY = Math.max(
                0,
                document.documentElement.scrollHeight - window.innerHeight
            );
            const y = Math.min(Math.max(window.scrollY, 0), maxY);
            const delta = y - lastY;
            lastY = y;

            if (delta === 0) {
                return;
            }

            // Direction flips reset the accumulator so slow mixed wiggling
            // has to commit to one direction before anything toggles.
            if ((delta > 0) !== (accumulated > 0)) {
                accumulated = 0;
            }
            accumulated += delta;

            if (y <= TOP_ZONE_PX) {
                setHidden(false);
            } else if (accumulated > HIDE_AFTER_PX) {
                setHidden(true);
            } else if (accumulated < -SHOW_AFTER_PX) {
                setHidden(false);
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
        // Re-arming on pathname keeps the bars visible after navigation even
        // when the browser preserves the scroll position.
    }, [enabled, pathname]);

    return hidden;
}
